import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGetShow, apiGetShowRoadmap, apiGetShowCheckins, apiSetShowCheckin, type ShowDTO, type RoadmapItemDTO, type ShowCheckinDTO } from '../api';
import { usePageLoadErrorText } from '../components/PageLoadError';

function parseHM(s?: string): number | null {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(s).trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
function fmtHM(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = Math.floor(min % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fmtCountdown(totalMin: number): string {
  const abs = Math.abs(totalMin);
  const h = Math.floor(abs / 60);
  const m = Math.floor(abs % 60);
  const sign = totalMin < 0 ? '-' : '';
  return h > 0 ? `${sign}${h}h ${m.toString().padStart(2, '0')}` : `${sign}${m} min`;
}

/** Map a show's event type to an icon (echo of ShowsPage.typeIcon). */
function evIcon(et?: string): string {
  const e = (et || '').toLowerCase();
  if (e === 'show' || e === 'concert') return '🎫';
  if (e === 'festival') return '🎪';
  if (e === 'travel') return '🚐';
  if (e === 'off') return '🚫';
  return '📌';
}

/**
 * Show Time — full-screen, dark, high-contrast live view for the night of a
 * show (2026-09-05). Mobile-first: huge text, tappable, readable in the dark.
 * Displays the show's running order (roadmap) with the CURRENT + NEXT step
 * highlighted and a live countdown, plus the key show facts. No time-realtime
 * push in this MVP — data refreshes on a short interval.
 */
export default function ShowTimePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [show, setShow] = useState<ShowDTO | null>(null);
  const [roadmap, setRoadmap] = useState<RoadmapItemDTO[]>([]);
  const [checkins, setCheckins] = useState<ShowCheckinDTO[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Lot 3a — a FAILED LOAD (the show itself could not be read) is a different
  // thing from a show that does not exist, and it must be readable AND
  // retryable: this screen is opened in a dark venue, on a phone, right before
  // doors. `retryToken` re-arms the fetch below; the page is OUTSIDE the app
  // layout, so it keeps its own inline error surface and only reuses the SHARED
  // wording geared by `usePageLoadErrorText` — never a second message.
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const loadErrorText = usePageLoadErrorText();
  const [now, setNow] = useState(() => new Date());

  // Refresh "now" every 30s (no WS in MVP) so countdown / current-step stay fresh.
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    apiGetShow(id)
      .then((s) => { if (!cancelled) setShow(s); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    apiGetShowRoadmap(id)
      .then((r) => { if (!cancelled) setRoadmap(r); })
      .catch(() => {});
    apiGetShowCheckins(id)
      .then((c) => { if (!cancelled) setCheckins(c); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, retryToken]);

  // Toggle a check-in step: optimistic flip, revert on error, keep the
  // server's saved flag on success. (Persisted via PUT /checkins/:rid.)
  const toggleCheckin = (item: ShowCheckinDTO) => {
    if (!id || busyId !== null) return;
    const rid = String(item.roadmap_item_id);
    const next = item.done ? 0 : 1;
    const prev = item.done;
    setBusyId(rid);
    setCheckins((cs) => cs.map((c) => c.roadmap_item_id === item.roadmap_item_id ? { ...c, done: next } : c));
    apiSetShowCheckin(id, rid, next)
      .then((saved) => {
        setCheckins((cs) => cs.map((c) => c.roadmap_item_id === item.roadmap_item_id ? { ...c, done: saved.done, checkin_id: saved.checkin_id } : c));
      })
      .catch(() => {
        setCheckins((cs) => cs.map((c) => c.roadmap_item_id === item.roadmap_item_id ? { ...c, done: prev } : c));
      })
      .finally(() => setBusyId(null));
  };

  const steps = useMemo(() => {
    return [...roadmap]
      .filter((s) => s.activity || s.time_from || s.time_to)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [roadmap]);

  // Determine current index: the last step whose time_from <= now (or the one
  // whose window contains now). Steps without a time fall at their sort position.
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const currentIdx = useMemo(() => {
    let idx = -1;
    steps.forEach((s, i) => {
      const from = parseHM(s.time_from);
      if (from !== null && from <= nowMin) idx = i;
      else if (from === null && idx === -1) idx = i; // first untimed step if nothing yet
    });
    return idx;
  }, [steps, nowMin]);

  const current = currentIdx >= 0 ? steps[currentIdx] : null;
  const next = currentIdx >= 0 && currentIdx + 1 < steps.length ? steps[currentIdx + 1] : null;

  // Countdown to the NEXT step's start (or to the current step's end if in it).
  const nextFrom = next ? parseHM(next.time_from) : null;
  const curTo = current ? parseHM(current.time_to) : null;
  const targetMin = nextFrom !== null ? nextFrom : (curTo !== null ? curTo : null);
  const countdownMin = targetMin !== null ? targetMin - nowMin : null;

  // Footer clock: LOCAL time of the already-refreshed `now` state (2 digits),
  // NOT a bare date — a second date next to the header's show date reads as a
  // contradiction to the reader. Reuse `now`, never a second timer.
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const time = `${hh}:${mm}`;

  // Sort-order labels for category color accents.
  //
  // PALETTE FIXE (LOT U1b) — cet écran est le SEUL rendu hors AppLayout et il est
  // volontairement SOMBRE dans les deux thèmes (mode « nuit du concert » : on le
  // lit sur la scène, pas au bureau — voir references/show-time-mode.md). Ce n'est
  // donc pas de l'UI thématisée : les valeurs ci-dessous forment une palette
  // LOCALE, calibrée et MESURÉE sur son propre fond (jamais sur --card) :
  //   #f2f2f2 sur #0a0c10 = 17.5 · #9aa3ae = 7.7 · #aaa = 6.9 · #7d8590 = 5.3
  //   #888 = 5.4 · #6ee7a0 sur #1b3a2b = 8.1 · #b7c4bf = 6.9
  //   #8a929c sur #0a0c10 = 6.2  (remplace #4a4f58 = 2.38 : c'était le SEUL texte
  //   illisible de l'écran, le pied « Updated HH:MM »)
  // Les tokenner en var(--*) rendrait l'écran CLAIR en thème clair : ce serait un
  // changement de design, pas une correction de contraste.
  const bg = '#0a0c10';

  if (error) {
    return (
      <div data-theme-free="fixed-dark" style={{ background: bg, color: '#eee', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t('showTime.show')}</p>
        {/* Shared wording (one key set for every page), readable in the dark —
            this screen is outside the app layout, so the generic panel would
            lose its high-contrast styling and its Back button. */}
        <p style={{ fontSize: 18, color: '#ffb54d', margin: '14px 0 0', maxWidth: 460 }}>{loadErrorText}</p>
        <p style={{ fontSize: 14, color: '#9aa3ae', margin: '8px 0 0', maxWidth: 460 }}>{error}</p>
        <button
          onClick={() => setRetryToken((n) => n + 1)}
          style={{ background: '#1b3a2b', color: '#6ee7a0', border: '1px solid #2f7a4f', borderRadius: 12, padding: '14px 28px', fontSize: 20, marginTop: 20 }}
        >
          {t('pageLoadError.retry')}
        </button>
        <button onClick={() => navigate(-1)} style={{ background: '#222', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 28px', fontSize: 20, marginTop: 12 }}>← Back</button>
      </div>
    );
  }

  return (
    <div data-theme-free="fixed-dark" style={{ background: bg, color: '#f2f2f2', minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '20px 16px', boxSizing: 'border-box', maxWidth: 520, margin: '0 auto' }}>
      {/* Header: show name + date */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1 }}>{evIcon(show?.event_type)} {show?.name || t('showTime.show')}</div>
          {show?.venue_name && <div style={{ fontSize: 16, color: '#aaa', marginTop: 4 }}>{show.venue_name} · {show.city || ''}</div>}
        </div>
        <button onClick={() => navigate(-1)} style={{ background: '#1c1f26', color: '#ccc', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 18, flexShrink: 0 }} aria-label="Back">✕</button>
      </div>

      {/* Door time fact */}
      <div style={{ display: 'flex', gap: 16, margin: '14px 0 8px', fontSize: 18 }}>
        {show?.door_time && <div><span style={{ color: '#888' }}>{t('showTime.doors')}</span> <strong style={{ fontSize: 22 }}>{show.door_time}</strong></div>}
      </div>

      {/* Live countdown */}
      {countdownMin !== null && (
        <div style={{ background: '#13161c', border: '1px solid #23262e', borderRadius: 18, padding: '16px 20px', margin: '8px 0 18px' }}>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#7d8590' }}>
            {next ? t('showTime.nextUp') : t('showTime.untilEnd')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
            <span style={{ fontSize: 46, fontWeight: 800, color: countdownMin !== null && countdownMin <= 10 ? '#ffb54d' : '#6ee7a0' }}>{countdownMin !== null ? fmtCountdown(countdownMin) : '—'}</span>
            {next && <span style={{ fontSize: 22, fontWeight: 700 }}>{next.activity}</span>}
          </div>
          {next && nextFrom !== null && <div style={{ color: '#9aa3ae', fontSize: 14, marginTop: 2 }}>{fmtHM(nextFrom)}</div>}
        </div>
      )}

      {/* Current step */}
      {current && (
        <div style={{ background: '#1b3a2b', border: '1px solid #2f7a4f', borderRadius: 18, padding: '18px 20px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#6ee7a0' }}>{t('showTime.current')}</div>
          <div style={{ fontSize: 30, fontWeight: 800, marginTop: 2 }}>{current.activity || '—'}</div>
          <div style={{ fontSize: 18, color: '#b7c4bf', marginTop: 4 }}>
            {[current.time_from, current.time_to].filter(Boolean).join(' → ')}
            {current.responsible ? ` · ${current.responsible}` : ''}
          </div>
        </div>
      )}

      {/* Persistent check-in: giant tappable checkboxes for the checkboxable
          roadmap steps (load-in / show / soundcheck). Optimistic toggle. */}
      <div style={{ margin: '6px 0 16px' }}>
        <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#7d8590', margin: '8px 0 8px' }}>
          {t('showTime.checkinTitle')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {checkins.map((c) => {
            const done = c.done === 1;
            const busy = busyId === String(c.roadmap_item_id);
            return (
              <button
                key={String(c.roadmap_item_id)}
                onClick={() => toggleCheckin(c)}
                disabled={busy}
                aria-pressed={done}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                  textAlign: 'left', cursor: 'pointer', borderRadius: 16,
                  padding: '14px 16px', background: done ? '#1b3a2b' : '#13161c',
                  border: done ? '1px solid #2f7a4f' : '1px solid #23262e',
                  color: '#f2f2f2', opacity: busy ? 0.5 : 1,
                  transition: 'background .12s ease, border-color .12s ease',
                }}
              >
                <div style={{
                  width: 34, height: 34, flexShrink: 0, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, fontWeight: 800, color: done ? '#0a0c10' : 'transparent',
                  background: done ? '#2f7a4f' : '#1c1f26',
                  border: done ? 'none' : '1px solid #2c313a',
                }}>
                  ✓
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.15 }}>
                    {c.activity || '—'}
                  </div>
                  <div style={{ fontSize: 14, color: '#9aa3ae', marginTop: 3 }}>
                    {c.time_from ? fmtHM(parseHM(c.time_from)!) : ''}
                    {c.time_from && c.responsible ? ' · ' : ''}
                    {c.responsible || ''}
                  </div>
                </div>
              </button>
            );
          })}
          {checkins.length === 0 && (
            <div style={{ color: '#7d8590', fontSize: 16, padding: 16, textAlign: 'center' }}>
              {t('showTime.checkinEmpty')}
            </div>
          )}
        </div>
        {checkins.length > 0 && (
          <div style={{ fontSize: 14, color: '#6ee7a0', marginTop: 8 }}>
            {t('showTime.checkinDoneCount', { done: checkins.filter((c) => c.done === 1).length, total: checkins.length })}
          </div>
        )}
      </div>

      {/* Upcoming mini-list */}
      <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#7d8590', margin: '8px 0 6px' }}>{t('showTime.upcoming')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {steps.map((s, i) => {
          const isPast = currentIdx !== -1 && i <= currentIdx;
          const isCur = i === currentIdx;
          return (
            <div key={String(s.id)} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 12,
              background: isCur ? 'transparent' : '#11141a',
              border: isCur ? 'none' : '1px solid #1c2028',
              opacity: isPast && !isCur ? 0.4 : 1,
              fontSize: isCur ? 0 : 15,
            }}>
              <div style={{ width: 52, textAlign: 'right', color: '#7d8590', fontVariantNumeric: 'tabular-nums' }}>{s.time_from ? fmtHM(parseHM(s.time_from)!) : ''}</div>
              <div style={{ flex: 1, fontWeight: isPast && !isCur ? 400 : 700 }}>{s.activity || '—'}</div>
              {s.responsible && <div style={{ color: '#7d8590', fontSize: 13 }}>{s.responsible}</div>}
              {isCur && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#6ee7a0' }} />}
            </div>
          );
        })}
        {steps.length === 0 && (
          <div style={{ color: '#7d8590', fontSize: 16, padding: 16, textAlign: 'center' }}>
            {t('showTime.noSchedule')}
          </div>
        )}
      </div>

      <div style={{ marginTop: 20, textAlign: 'center', color: '#8a929c', fontSize: 12 }}>
        {t('showTime.updatedAt', { time })} · {t('showTime.refreshes')}
      </div>
    </div>
  );
}
